import React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Handshake,
  X,
  CheckCircle2,
  Clock,
  Wallet,
  RefreshCw,
  ShieldCheck,
  UserPlus,
  PlayCircle,
} from "lucide-react";

const SECTIONS = [
  {
    n: "1",
    icon: UserPlus,
    title: "Como aderir ao sistema",
    paragraphs: [
      "Para participar do Faz Depois Paga, a escola deverá, inicialmente, contratar um serviço convencional com a AMC Escolas.",
      "O primeiro serviço deverá ser executado normalmente, com a emissão da respectiva nota fiscal e a realização do pagamento pela escola.",
      "Após a conclusão do serviço, a emissão da nota fiscal e a confirmação do pagamento, estando todas as obrigações devidamente regularizadas, a escola poderá ser cadastrada e ativada no sistema Faz Depois Paga.",
      "A ativação está condicionada à conclusão e à regularização integral desse primeiro serviço.",
    ],
  },
  {
    n: "2",
    icon: PlayCircle,
    title: "Como funciona após a ativação",
    paragraphs: [
      "Depois de habilitada, a escola poderá solicitar pequenos serviços de manutenção à AMC Escolas, conforme as condições estabelecidas para o seu cadastro.",
      "Os serviços poderão ser solicitados ao longo do período de utilização, respeitando dois limites previamente definidos:",
    ],
    bullets: [
      { icon: Clock, label: "Limite de prazo", desc: "período máximo durante o qual a escola poderá utilizar o sistema." },
      { icon: Wallet, label: "Limite de valor", desc: "valor máximo acumulado permitido para os serviços realizados durante o ciclo." },
    ],
    tail: "Os serviços solicitados e executados serão registrados no sistema, permitindo o acompanhamento dos atendimentos e dos valores acumulados.",
  },
  {
    n: "3",
    icon: CheckCircle2,
    title: "Quando o ciclo é encerrado?",
    paragraphs: [
      "O ciclo do Faz Depois Paga será encerrado quando qualquer um dos dois limites estabelecidos for atingido: o limite de prazo ou o limite de valor.",
      "Ao atingir uma dessas condições, será necessário realizar o fechamento do ciclo, com a emissão da nota fiscal correspondente aos serviços acumulados e a regularização do pagamento.",
      "O prazo e o limite financeiro funcionam de forma independente: basta atingir um deles para que o ciclo seja encerrado.",
    ],
  },
  {
    n: "4",
    icon: RefreshCw,
    title: "Como funciona a renovação do sistema?",
    paragraphs: [
      "Após o fechamento do ciclo, a emissão da nota fiscal e a regularização do pagamento, o sistema poderá ser reiniciado para um novo período de utilização.",
      "Com a renovação, a escola volta a contar com os limites de prazo e de valor definidos para o novo ciclo, podendo continuar solicitando pequenos serviços de manutenção conforme as regras do programa.",
      "A continuidade da utilização fica condicionada à regularização do ciclo anterior.",
    ],
  },
  {
    n: "5",
    icon: ShieldCheck,
    title: "Compromisso com organização e transparência",
    paragraphs: [
      "O Faz Depois Paga foi desenvolvido para simplificar a rotina das escolas e facilitar o atendimento de pequenas demandas de manutenção, mantendo o controle dos serviços executados, dos valores acumulados, dos prazos e dos fechamentos fiscais.",
    ],
  },
];

export default function FdpInfoDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[94vw] max-w-md p-0 overflow-hidden gap-0 max-h-[88vh] grid grid-rows-[auto_1fr] [&>button]:hidden"
        data-testid="fdp-info-dialog"
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-emerald-600 to-emerald-500 px-4 pt-4 pb-4 text-white">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Fechar"
            data-testid="fdp-info-close"
            className="absolute top-2.5 right-2.5 w-9 h-9 rounded-full bg-white/25 hover:bg-white/40 active:bg-white/50 backdrop-blur flex items-center justify-center transition-colors z-10"
          >
            <X className="w-5 h-5 text-white" strokeWidth={2.6} />
          </button>
          <div className="flex items-center gap-2 mb-2 pr-10">
            <div className="w-9 h-9 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center shrink-0">
              <Handshake className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/15 backdrop-blur">
              <span className="text-[9px] font-bold uppercase tracking-widest">
                AMC Escolas
              </span>
            </div>
          </div>
          <DialogTitle className="text-lg font-black text-white leading-tight">
            Sistema Faz Depois Paga
          </DialogTitle>
          <DialogDescription className="text-[12px] text-white/90 mt-1 leading-snug">
            Mais praticidade e organização para a manutenção da sua escola.
          </DialogDescription>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto px-4 py-4 bg-white">
          {/* Como funciona (overview) */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-1.5">
              Como funciona?
            </p>
            <p className="text-[12px] text-emerald-900 leading-relaxed">
              O <strong>Faz Depois Paga</strong> é um sistema criado para
              facilitar a contratação de pequenos serviços de manutenção pelas
              escolas, oferecendo mais praticidade, agilidade e organização
              financeira. Por meio desse sistema, a escola habilitada pode
              solicitar pequenos serviços ao longo de um período determinado,
              acumulando os valores em um único controle até atingir o limite
              de prazo ou de valor estabelecido.
            </p>
          </div>

          {/* Numbered sections */}
          <div className="mt-4 space-y-4">
            {SECTIONS.map((s) => (
              <section key={s.n} data-testid={`fdp-section-${s.n}`}>
                <div className="flex items-center gap-2.5 mb-2">
                  <div className="relative w-9 h-9 shrink-0 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-sm">
                    <s.icon className="w-4 h-4 text-white" strokeWidth={2.4} />
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-white text-emerald-600 text-[9px] font-black flex items-center justify-center border border-emerald-200">
                      {s.n}
                    </span>
                  </div>
                  <h3 className="text-[13px] font-black text-slate-900 leading-tight">
                    {s.title}
                  </h3>
                </div>
                <div className="pl-11 space-y-2">
                  {s.paragraphs.map((p, i) => (
                    <p key={i} className="text-[12px] text-slate-600 leading-relaxed">
                      {p}
                    </p>
                  ))}
                  {s.bullets && (
                    <ul className="space-y-1.5 pt-1">
                      {s.bullets.map((b) => (
                        <li key={b.label} className="flex gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
                          <div className="w-7 h-7 shrink-0 rounded-md bg-white border border-slate-200 flex items-center justify-center">
                            <b.icon className="w-3.5 h-3.5 text-emerald-600" strokeWidth={2.4} />
                          </div>
                          <p className="text-[11.5px] text-slate-700 leading-snug">
                            <strong className="text-slate-900">{b.label}:</strong>{" "}
                            {b.desc}
                          </p>
                        </li>
                      ))}
                    </ul>
                  )}
                  {s.tail && (
                    <p className="text-[12px] text-slate-600 leading-relaxed">
                      {s.tail}
                    </p>
                  )}
                </div>
              </section>
            ))}
          </div>

          {/* Closing tagline */}
          <div className="mt-4 bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-lg p-3 text-center">
            <p className="text-[11.5px] text-emerald-900 leading-relaxed">
              <strong>AMC Escolas</strong> — mais praticidade para a manutenção
              da sua escola, com <strong>organização</strong>,{" "}
              <strong>transparência</strong> e <strong>confiança</strong>.
            </p>
          </div>

          <Button
            onClick={() => onOpenChange(false)}
            className="w-full h-11 mt-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm"
            data-testid="fdp-info-ok"
          >
            Entendi, voltar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
